import { useMemo } from "react";
import { useCreateFilterStateBranch } from "../../state/FilterContext";
import { useCreateInternalStateBranch, useStoredItems } from "../../state/InternalContext";
import { FilterState } from "../../state/state-models";
import { InternalState } from "../../state/state-models";

interface ForChildrenProps {
  store: string,
}

/***
 * A component that renders its content for each child of a stored item.
 * It makes it possible to render a recursive structure, such as a table of content tree.
 * It must be used inside a ForEach component. The template of the ForEach component should
 * include a ForChildren component and the same template is reused for all children recursively.
 */
export function ForChildren({ store }: ForChildrenProps) {
  const storedItems = useStoredItems();
  const createInternalStateBranch = useCreateInternalStateBranch();
  const createFilterStateBranch = useCreateFilterStateBranch();
  const item = storedItems[store];

  const internalBranchComponents = useMemo(() => {
    return item.children?.map((childItem) => {
      const nextInternalState = childItem.state as InternalState || {};
      nextInternalState.storedItems = {
        ...nextInternalState.storedItems,
        [store]: { ...childItem, template: item.template },
      };

      return createInternalStateBranch(nextInternalState);
    }) || [];
  }, [item, createInternalStateBranch, store]);

  const filterBranchComponents = useMemo(() => {
    return item.children?.map((childItem) => {
      return createFilterStateBranch(childItem.state as FilterState || {});
    }) || [];
  }, [item, createFilterStateBranch]);

  const output: Array<React.ReactNode> = item.children?.map((childItem, index) => {
    const InternalBranchComponent = internalBranchComponents[index];
    const FilterBranchComponent = filterBranchComponents[index];
    return (
      <InternalBranchComponent key={childItem.value.toString()}>
        <FilterBranchComponent key={childItem.value.toString()}>
          {item.template}
        </FilterBranchComponent>
      </InternalBranchComponent>
    );
  }) || [];

  return <>{output}</>;
}
